/**
 * Chat route guards -- keep non-chat-id segments out of the chat API.
 *
 *   - NewChatRoute (/chat/new): creates a chat the same way as the sidebar "+" and
 *     mod+n (useCreateChat().mutate({})) and replaces the URL with /chat/<id>.
 *   - ChatIdGuard (/chat/:chatId): a chat id that is not a UUID never reaches
 *     ChatPage (so it never becomes the active chat nor hits /chats/<id>); the
 *     URL is replaced with /chat.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { useCreateChat } from '@/hooks/useChats';
import { ShellContentFallback } from '@/layouts/chatShell/ShellContent';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isChatId(value: string | undefined): value is string {
  return value !== undefined && UUID_RE.test(value);
}

export function NewChatRoute() {
  const navigate = useNavigate();
  const createChat = useCreateChat();
  const createChatMutateRef = useRef(createChat.mutate);
  createChatMutateRef.current = createChat.mutate;
  // StrictMode re-runs effects on the same instance: create only once.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    createChatMutateRef.current(
      {},
      {
        onSuccess: (chat) => navigate(`/chat/${chat.id}`, { replace: true }),
        onError: () => navigate('/chat', { replace: true }),
      },
    );
  }, [navigate]);

  return <ShellContentFallback />;
}

export function ChatIdGuard({ children }: { children: ReactNode }) {
  const { chatId } = useParams<{ chatId?: string }>();
  if (!isChatId(chatId)) return <Navigate to="/chat" replace />;
  return <>{children}</>;
}
